import { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { Search, X, Loader2, ChevronLeft, ChevronDown, SlidersHorizontal, Camera, Sparkles, Mic, MicOff, FileText, Info, Phone, HelpCircle, Truck, RotateCcw, Ruler, Gift, RefreshCw, BookOpen, Package, Shield, ScrollText, ShoppingCart, Store } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { Slider } from "@/components/ui/slider";
import {
  searchShopifyProductsWithRelated,
  searchShopifyCollections,
  ShopifyProduct,
  ShopifyCollection,
  SearchResult,
} from "@/lib/shopify";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "motion/react";
import { formatPrice } from "@/lib/utils";
import { useGlobalPreload } from "@/hooks/useGlobalPreload";
import { ImageSearchUploader, VisualMatch } from "@/components/ImageSearchUploader";

// Site pages configuration for search
const SITE_PAGES = [
  { name: 'אודות', path: '/about', keywords: ['אודות', 'אודותינו', 'מי אנחנו', 'אליס', 'about'], icon: Info, description: 'קצת עלינו ועל החזון שלנו' },
  { name: 'צור קשר', path: '/contact', keywords: ['צור קשר', 'טלפון', 'כתובת', 'מייל', 'contact'], icon: Phone, description: 'דרכים ליצור איתנו קשר' },
  { name: 'שאלות נפוצות', path: '/faq', keywords: ['שאלות', 'תשובות', 'FAQ', 'שאלות נפוצות', 'עזרה'], icon: HelpCircle, description: 'תשובות לשאלות הנפוצות ביותר' },
  { name: 'משלוחים', path: '/shipping', keywords: ['משלוח', 'משלוחים', 'זמן אספקה', 'דואר', 'shipping'], icon: Truck, description: 'מידע על משלוחים וזמני אספקה' },
  { name: 'החזרות', path: '/returns', keywords: ['החזרה', 'החלפה', 'החזרות', 'ביטול', 'returns'], icon: RotateCcw, description: 'מדיניות החזרות והחלפות' },
  { name: 'מדריך מידות', path: '/ring-size-guide', keywords: ['מידה', 'טבעת', 'מידות', 'גודל', 'size guide'], icon: Ruler, description: 'מדריך למציאת המידה המתאימה' },
  { name: 'כרטיס מתנה', path: '/gift-card', keywords: ['מתנה', 'גיפט קארד', 'שובר', 'gift card', 'gift'], icon: Gift, description: 'רכישת כרטיס מתנה' },
  { name: 'Trade In', path: '/trade-in', keywords: ['טרייד אין', 'trade in', 'החלפה', 'מחזור', 'זהב ישן'], icon: RefreshCw, description: 'החלפת תכשיטים ישנים בחדשים' },
  { name: 'בלוג', path: '/blog', keywords: ['בלוג', 'מאמרים', 'כתבות', 'blog', 'טיפים'], icon: BookOpen, description: 'מאמרים וטיפים מעולם התכשיטים' },
  { name: 'מעקב הזמנה', path: '/track-order', keywords: ['מעקב', 'הזמנה', 'סטטוס', 'תיקון', 'track'], icon: Package, description: 'מעקב אחרי הזמנות ותיקונים' },
  { name: 'נגישות', path: '/accessibility', keywords: ['נגישות', 'accessibility'], icon: Shield, description: 'הצהרת נגישות' },
  { name: 'פרטיות', path: '/privacy', keywords: ['פרטיות', 'מדיניות', 'privacy'], icon: ScrollText, description: 'מדיניות פרטיות' },
  { name: 'תקנון', path: '/terms', keywords: ['תקנון', 'תנאי שימוש', 'terms'], icon: FileText, description: 'תנאי שימוש באתר' },
  { name: 'עגלת קניות', path: '/cart', keywords: ['עגלה', 'סל קניות', 'cart'], icon: ShoppingCart, description: 'עגלת הקניות שלך' },
  { name: 'החנות', path: '/shop', keywords: ['חנות', 'כל המוצרים', 'shop', 'מוצרים'], icon: Store, description: 'כל המוצרים שלנו' },
];

function searchSitePages(query: string) {
  const q = query.trim().toLowerCase();
  if (q.length < 2) return [];
  return SITE_PAGES.filter(page =>
    page.name.toLowerCase().includes(q) ||
    page.keywords.some(kw => kw.toLowerCase().includes(q))
  );
}

// SpeechRecognition types
interface SpeechRecognitionEvent {
  results: { [index: number]: { [index: number]: { transcript: string } } };
}

declare global {
  interface Window {
    webkitSpeechRecognition: any;
    SpeechRecognition: any;
  }
}

export const SearchDialog = () => {
  const [open, setOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResult, setSearchResult] = useState<SearchResult | null>(null);
  const [categoryResults, setCategoryResults] = useState<ShopifyCollection[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const searchRequestIdRef = useRef(0);

  // Image search states
  const [isImageSearchMode, setIsImageSearchMode] = useState(false);
  const [isAnalyzingImage, setIsAnalyzingImage] = useState(false);
  const [imageSearchDescription, setImageSearchDescription] = useState<string | null>(null);
  const [visualMatches, setVisualMatches] = useState<VisualMatch[]>([]);
  const [isVisualSearchResult, setIsVisualSearchResult] = useState(false);

  // Filter states
  const [showFilters, setShowFilters] = useState(false);
  const [priceRange, setPriceRange] = useState<[number, number]>([0, 5000]);
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);

  // Voice search state
  const [isListening, setIsListening] = useState(false);
  const recognitionRef = useRef<any>(null);

  // Site page results
  const sitePageResults = useMemo(() => {
    return searchSitePages(searchQuery);
  }, [searchQuery]);

  // Use global preloaded data
  const { 
    isLoaded, 
    isLoading, 
    searchSettings, 
    defaultProducts, 
    allProducts,
    allCollections,
    preload 
  } = useGlobalPreload();

  // Ensure data is preloaded when component mounts
  useEffect(() => {
    preload();
  }, [preload]);

  // Get max price from all products for the slider
  const maxPrice = useMemo(() => {
    const prices = allProducts.map(p => parseFloat(p.priceRange?.minVariantPrice?.amount || "0"));
    return Math.max(...prices, 5000);
  }, [allProducts]);

  // Initialize price range when max price is calculated
  useEffect(() => {
    if (maxPrice > 0) {
      setPriceRange([0, Math.ceil(maxPrice)]);
    }
  }, [maxPrice]);

  // Get unique categories from collections
  const availableCategories = useMemo(() => {
    return allCollections.map(c => ({
      handle: c.node.handle,
      title: c.node.title,
      image: c.node.image?.url
    }));
  }, [allCollections]);

  const doSearch = useCallback(async (q: string) => {
    const requestId = ++searchRequestIdRef.current;
    setIsSearching(true);
    setHasSearched(false);

    try {
      const [result, collections] = await Promise.all([
        searchShopifyProductsWithRelated(q, 40),
        searchSettings.show_categories ? searchShopifyCollections(q) : Promise.resolve([]),
      ]);

      if (searchRequestIdRef.current !== requestId) return;

      setSearchResult(result);
      setCategoryResults(collections);
      setHasSearched(true);
    } catch (error) {
      console.error("Error searching:", error);
      if (searchRequestIdRef.current === requestId) {
        setSearchResult(null);
        setCategoryResults([]);
        setHasSearched(true);
      }
    } finally {
      if (searchRequestIdRef.current === requestId) {
        setIsSearching(false);
      }
    }
  }, [searchSettings.show_categories]);

  // Update query immediately; real searching is debounced in an effect
  const handleSearch = useCallback((query: string) => {
    setSearchQuery(query);
    if (query.trim().length >= 2) {
      setIsSearching(true); // Start loading immediately when typing
      setHasSearched(false);
    }
  }, []);

  // Debounced remote search (covers ALL products/collections, not a prefetched subset)
  useEffect(() => {
    if (!open) return;

    const q = searchQuery.trim();
    if (q.length < 2) {
      setSearchResult(null);
      setCategoryResults([]);
      setIsSearching(false);
      setHasSearched(false);
      return;
    }

    const timer = window.setTimeout(() => {
      void doSearch(q);
    }, 250);

    return () => window.clearTimeout(timer);
  }, [open, searchQuery, doSearch]);

  const handleClear = () => {
    setSearchQuery("");
    setSearchResult(null);
    setCategoryResults([]);
    setHasSearched(false);
    setImageSearchDescription(null);
    setVisualMatches([]);
    setIsVisualSearchResult(false);
  };

  const handleClose = () => {
    setOpen(false);
    setSearchQuery("");
    setSearchResult(null);
    setCategoryResults([]);
    setIsSearching(false);
    setHasSearched(false);
    setShowFilters(false);
    setSelectedCategory(null);
    setPriceRange([0, Math.ceil(maxPrice)]);
    setIsImageSearchMode(false);
    setIsAnalyzingImage(false);
    setImageSearchDescription(null);
    setVisualMatches([]);
    setIsVisualSearchResult(false);
  };

  // Image search handlers
  const handleImageAnalyzeComplete = useCallback((searchTerms: string[], description: string, matches?: VisualMatch[]) => {
    setIsImageSearchMode(false);
    setIsAnalyzingImage(false);
    setImageSearchDescription(description);
    
    // If we have visual matches, use them directly
    if (matches && matches.length > 0) {
      setVisualMatches(matches);
      setIsVisualSearchResult(true);
      setHasSearched(true);
      setSearchQuery(searchTerms[0] || "חיפוש ויזואלי");
    } else {
      // Fallback to text search
      const query = searchTerms[0] || "תכשיט";
      setSearchQuery(query);
      doSearch(query);
    }
  }, [doSearch]);

  const handleImageSearchCancel = useCallback(() => {
    setIsImageSearchMode(false);
    setIsAnalyzingImage(false);
  }, []);

  // Voice search handlers
  const startListening = useCallback(() => {
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SpeechRecognition) return;

    const recognition = new SpeechRecognition();
    recognition.lang = 'he-IL';
    recognition.continuous = false;
    recognition.interimResults = false;

    recognition.onresult = (event: SpeechRecognitionEvent) => {
      const transcript = event.results[0][0].transcript;
      setSearchQuery(transcript);
      setIsListening(false);
      if (transcript.trim().length >= 2) {
        setIsSearching(true);
        setHasSearched(false);
      }
    };

    recognition.onerror = () => setIsListening(false);
    recognition.onend = () => setIsListening(false);

    recognitionRef.current = recognition;
    recognition.start();
    setIsListening(true);
  }, []);

  const stopListening = useCallback(() => {
    recognitionRef.current?.stop();
    setIsListening(false);
  }, []);

  const clearFilters = () => {
    setSelectedCategory(null);
    setPriceRange([0, Math.ceil(maxPrice)]);
  };

  const hasActiveFilters = selectedCategory !== null || priceRange[0] > 0 || priceRange[1] < Math.ceil(maxPrice);

  // Get exact matches and related products
  const exactMatches = searchResult?.exactMatches || [];
  const relatedProducts = searchResult?.relatedProducts || [];
  const fallbackTerm = searchResult?.fallbackTerm;
  const searchTerms = searchResult?.searchTermsUsed || [];

  // Products to display when not searching - use preloaded data, then apply filters
  const displayProducts = useMemo(() => {
    let products: ShopifyProduct[];
    
    if (searchQuery.trim().length >= 2) {
      // Combine exact matches with related (exact first)
      products = [...exactMatches, ...relatedProducts.filter(p => !exactMatches.some(e => e.id === p.id))];
    } else {
      products = defaultProducts.length > 0 ? defaultProducts : allProducts.slice(0, 12);
    }

    // Apply price filter
    products = products.filter(product => {
      const price = parseFloat(product.priceRange?.minVariantPrice?.amount || "0");
      return price >= priceRange[0] && price <= priceRange[1];
    });

    return products;
  }, [searchQuery, exactMatches, relatedProducts, defaultProducts, allProducts, priceRange]);

  // Separate exact and related for display purposes
  const displayExact = useMemo(() => {
    return exactMatches.filter(product => {
      const price = parseFloat(product.priceRange?.minVariantPrice?.amount || "0");
      return price >= priceRange[0] && price <= priceRange[1];
    });
  }, [exactMatches, priceRange]);

  const displayRelated = useMemo(() => {
    return relatedProducts
      .filter(p => !exactMatches.some(e => e.id === p.id))
      .filter(product => {
        const price = parseFloat(product.priceRange?.minVariantPrice?.amount || "0");
        return price >= priceRange[0] && price <= priceRange[1];
      });
  }, [relatedProducts, exactMatches, priceRange]);

  const hasResults = searchQuery.trim().length >= 2;
  const showCategories = hasResults && categoryResults.length > 0 && searchSettings.show_categories;
  const showRelatedSection = hasResults && hasSearched && !isSearching && displayExact.length < 4 && displayRelated.length > 0;

  return (
    <Dialog open={open} onOpenChange={(isOpen) => {
      setOpen(isOpen);
      if (!isOpen) {
        setSearchQuery("");
        setSearchResult(null);
        setCategoryResults([]);
        setIsSearching(false);
        setHasSearched(false);
        setShowFilters(false);
        setSelectedCategory(null);
        setPriceRange([0, Math.ceil(maxPrice)]);
        setIsImageSearchMode(false);
        setIsAnalyzingImage(false);
        setImageSearchDescription(null);
        setVisualMatches([]);
        setIsVisualSearchResult(false);
      }
    }}>
      <DialogTrigger asChild>
        <Button variant="ghost" size="icon" className="hover:bg-transparent" aria-label="חיפוש מוצרים">
          <Search className="h-5 w-5 text-primary" strokeWidth={1.5} />
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-4xl max-h-[90vh] sm:max-h-[85vh] flex flex-col p-4 sm:p-6" dir="rtl">
        <DialogHeader className="pb-2">
          <DialogTitle className="text-right text-lg sm:text-xl font-light">חיפוש מוצרים</DialogTitle>
        </DialogHeader>
        
        {/* Image Search Mode */}
        {isImageSearchMode ? (
          <div className="py-4">
            <ImageSearchUploader
              onAnalyzeComplete={handleImageAnalyzeComplete}
              onCancel={handleImageSearchCancel}
              isAnalyzing={isAnalyzingImage}
              setIsAnalyzing={setIsAnalyzingImage}
            />
          </div>
        ) : (
          <>
            {/* Search Input */}
            <div className="relative">
              <Search className="absolute right-3 top-3 h-5 w-5 text-muted-foreground" />
              <Input
                type="text"
                placeholder="חפש תכשיטים לפי שם..."
                value={searchQuery}
                onChange={(e) => handleSearch(e.target.value)}
                className="pr-10 pl-36 text-right h-12 text-base"
                autoFocus
              />
              <div className="absolute left-2 top-2 flex items-center gap-0">
                {/* Voice search button */}
                {(window.SpeechRecognition || window.webkitSpeechRecognition) && (
                  <Button
                    variant="ghost"
                    size="icon"
                    className={`h-8 w-8 relative transition-colors ${isListening ? 'text-destructive hover:text-destructive/80 hover:bg-destructive/10' : 'text-primary hover:text-primary/80 hover:bg-primary/10'}`}
                    onClick={isListening ? stopListening : startListening}
                    title={isListening ? 'הפסק הקלטה' : 'חיפוש קולי'}
                  >
                    {isListening ? <MicOff className="h-4 w-4 z-10 relative" /> : <Mic className="h-4 w-4" />}
                    {isListening && (
                      <>
                        <span className="absolute inset-0 rounded-full border-2 border-destructive/60 animate-ping" />
                        <span className="absolute inset-1 rounded-full bg-destructive/10 animate-pulse" />
                      </>
                    )}
                  </Button>
                )}
                <div className="w-px h-4 bg-border" />
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8 text-primary hover:text-primary/80 hover:bg-primary/10"
                  onClick={() => setIsImageSearchMode(true)}
                  title="חיפוש לפי תמונה"
                >
                  <Camera className="h-4 w-4" />
                </Button>
                <div className="w-px h-4 bg-border" />
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8 text-primary hover:text-primary/80 hover:bg-primary/10"
                  onClick={() => setShowFilters(!showFilters)}
                  title="סינון חכם"
                >
                  <SlidersHorizontal className="h-4 w-4" />
                </Button>
                {searchQuery && (
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8"
                    onClick={handleClear}
                  >
                    <X className="h-4 w-4" />
                  </Button>
                )}
              </div>
            </div>

            {/* Image search description badge */}
            {imageSearchDescription && (
              <div className="flex items-center gap-2 text-sm bg-primary/10 text-primary px-3 py-2 rounded-lg">
                {isVisualSearchResult ? (
                  <Sparkles className="h-4 w-4" />
                ) : (
                  <Camera className="h-4 w-4" />
                )}
                <span>
                  {isVisualSearchResult 
                    ? `חיפוש ויזואלי מתקדם: ${imageSearchDescription}` 
                    : `חיפוש לפי תמונה: ${imageSearchDescription}`}
                </span>
                {visualMatches.length > 0 && (
                  <span className="text-xs bg-primary/20 px-2 py-0.5 rounded-full">
                    {visualMatches.length} תוצאות
                  </span>
                )}
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-5 w-5 mr-auto"
                  onClick={handleClear}
                >
                  <X className="h-3 w-3" />
                </Button>
              </div>
            )}

        {/* Filters Section */}
        <Collapsible open={showFilters} onOpenChange={setShowFilters}>
          <CollapsibleContent className="space-y-4 pt-3 pb-2">
            <div className="bg-muted/30 rounded-lg p-3 sm:p-4 space-y-4">
              {/* Price Range Filter */}
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium">טווח מחירים</span>
                  <span className="text-xs text-muted-foreground">
                    ₪{priceRange[0].toLocaleString()} - ₪{priceRange[1].toLocaleString()}
                  </span>
                </div>
                <Slider
                  value={priceRange}
                  onValueChange={(value) => setPriceRange(value as [number, number])}
                  min={0}
                  max={Math.ceil(maxPrice)}
                  step={50}
                  className="w-full"
                />
                <div className="flex justify-between text-xs text-muted-foreground">
                  <span>₪0</span>
                  <span>₪{Math.ceil(maxPrice).toLocaleString()}</span>
                </div>
              </div>

              {/* Category Filter */}
              <div className="space-y-2">
                <span className="text-sm font-medium block">קטגוריה</span>
                <div className="flex flex-wrap gap-2 max-h-24 overflow-y-auto">
                  <button
                    onClick={() => setSelectedCategory(null)}
                    className={`px-3 py-1.5 rounded-full text-xs sm:text-sm border transition-all ${
                      selectedCategory === null
                        ? 'bg-primary text-primary-foreground border-primary'
                        : 'bg-background border-border hover:border-primary/50'
                    }`}
                  >
                    הכל
                  </button>
                  {availableCategories.slice(0, 8).map((cat) => (
                    <button
                      key={cat.handle}
                      onClick={() => setSelectedCategory(cat.handle)}
                      className={`px-3 py-1.5 rounded-full text-xs sm:text-sm border transition-all ${
                        selectedCategory === cat.handle
                          ? 'bg-primary text-primary-foreground border-primary'
                          : 'bg-background border-border hover:border-primary/50'
                      }`}
                    >
                      {cat.title}
                    </button>
                  ))}
                </div>
              </div>

              {/* Clear Filters */}
              {hasActiveFilters && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={clearFilters}
                  className="w-full sm:w-auto"
                >
                  נקה סינון
                </Button>
              )}
            </div>
          </CollapsibleContent>
        </Collapsible>

        {/* Results count - only show when search completed */}
        {hasResults && hasSearched && !isSearching && (
          <div className="text-sm text-muted-foreground text-right flex items-center gap-2 py-1">
            <span>נמצאו {displayProducts.length} מוצרים</span>
            {sitePageResults.length > 0 && <span>• {sitePageResults.length} עמודים</span>}
            {showCategories && <span>• {categoryResults.length} קטגוריות</span>}
            {hasActiveFilters && <span className="text-primary text-xs">(מסונן)</span>}
          </div>
        )}

        <div className="mt-2 flex-1 min-h-0 overflow-y-auto">
          {!isLoaded || isLoading ? (
            <div className="flex items-center justify-center py-16">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
            </div>
          ) : isVisualSearchResult && visualMatches.length > 0 ? (
            // Visual search results with similarity scores
            <div className="space-y-4">
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <Sparkles className="h-4 w-4 text-primary" />
                <span>תוצאות מדורגות לפי דמיון ויזואלי</span>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2 sm:gap-4 p-1">
                <AnimatePresence mode="popLayout">
                  {visualMatches.map((match, index) => (
                    <motion.div
                      key={match.productHandle}
                      initial={{ opacity: 0, scale: 0.9 }}
                      animate={{ opacity: 1, scale: 1 }}
                      exit={{ opacity: 0, scale: 0.9 }}
                      transition={{ duration: 0.2, delay: index * 0.05 }}
                    >
                      <Link
                        to={`/products/${match.productHandle}`}
                        onClick={handleClose}
                        className="group block bg-background rounded-lg border border-border/50 overflow-hidden hover:border-primary/50 hover:shadow-lg transition-all duration-300"
                      >
                        <div className="aspect-square bg-secondary/10 overflow-hidden relative">
                          <img
                            src={match.productImage}
                            alt={match.productTitle}
                            loading="lazy"
                            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                          />
                          {/* Similarity score badge */}
                          <div className={`absolute top-2 left-2 px-2 py-1 rounded-full text-xs font-medium ${
                            match.similarityScore >= 80 
                              ? 'bg-green-500/90 text-white' 
                              : match.similarityScore >= 60 
                                ? 'bg-yellow-500/90 text-white'
                                : 'bg-orange-500/90 text-white'
                          }`}>
                            {match.similarityScore}% דומה
                          </div>
                        </div>
                        <div className="p-2 sm:p-3 text-right">
                          <h4 className="font-light text-xs sm:text-sm line-clamp-2 mb-1 group-hover:text-primary transition-colors">
                            {match.productTitle}
                          </h4>
                          <p className="text-primary font-medium text-xs sm:text-sm">
                            ₪{parseFloat(match.productPrice).toLocaleString()}
                          </p>
                          {match.matchReason && (
                            <p className="text-xs text-muted-foreground mt-1 line-clamp-2">
                              {match.matchReason}
                            </p>
                          )}
                        </div>
                      </Link>
                    </motion.div>
                  ))}
                </AnimatePresence>
              </div>
            </div>
          ) : (
            <>
              {/* Site Pages Section */}
              {sitePageResults.length > 0 && !isSearching && (
                <div className="mb-4 sm:mb-6">
                  <h3 className="text-sm font-medium text-muted-foreground mb-2 sm:mb-3 text-right">עמודים באתר</h3>
                  <div className="flex flex-wrap gap-2">
                    <AnimatePresence mode="popLayout">
                      {sitePageResults.map((page, index) => {
                        const IconComp = page.icon;
                        return (
                          <motion.div
                            key={page.path}
                            initial={{ opacity: 0, scale: 0.9 }}
                            animate={{ opacity: 1, scale: 1 }}
                            exit={{ opacity: 0, scale: 0.9 }}
                            transition={{ duration: 0.15, delay: index * 0.02 }}
                          >
                            <Link
                              to={page.path}
                              onClick={handleClose}
                              className="group flex items-center gap-2 px-3 sm:px-4 py-1.5 sm:py-2 rounded-full border border-border/50 bg-background hover:border-primary/50 hover:bg-primary/5 transition-all duration-200"
                            >
                              <IconComp className="h-4 w-4 text-primary" />
                              <div className="flex flex-col">
                                <span className="text-xs sm:text-sm group-hover:text-primary transition-colors">{page.name}</span>
                                <span className="text-[10px] text-muted-foreground/70 hidden sm:block">{page.description}</span>
                              </div>
                              <ChevronLeft className="h-3 w-3 sm:h-4 sm:w-4 text-muted-foreground group-hover:text-primary transition-colors" />
                            </Link>
                          </motion.div>
                        );
                      })}
                    </AnimatePresence>
                  </div>
                </div>
              )}

              {/* Categories Section */}
              {showCategories && !isSearching && (
                <div className="mb-4 sm:mb-6">
                  <h3 className="text-sm font-medium text-muted-foreground mb-2 sm:mb-3 text-right">קטגוריות</h3>
                  <div className="flex flex-wrap gap-2">
                    <AnimatePresence mode="popLayout">
                      {categoryResults.slice(0, 6).map((collection, index) => (
                        <motion.div
                          key={collection.node.id}
                          initial={{ opacity: 0, scale: 0.9 }}
                          animate={{ opacity: 1, scale: 1 }}
                          exit={{ opacity: 0, scale: 0.9 }}
                          transition={{ duration: 0.15, delay: index * 0.02 }}
                        >
                          <Link
                            to={`/collections/${collection.node.handle}`}
                            onClick={handleClose}
                            className="group flex items-center gap-2 px-3 sm:px-4 py-1.5 sm:py-2 rounded-full border border-border/50 bg-background hover:border-primary/50 hover:bg-primary/5 transition-all duration-200"
                          >
                            {collection.node.image && (
                              <img
                                src={collection.node.image.url}
                                alt={collection.node.title}
                                loading="lazy"
                                className="w-5 h-5 sm:w-6 sm:h-6 rounded-full object-cover"
                              />
                            )}
                            <span className="text-xs sm:text-sm group-hover:text-primary transition-colors">
                              {collection.node.title}
                            </span>
                            <ChevronLeft className="h-3 w-3 sm:h-4 sm:w-4 text-muted-foreground group-hover:text-primary transition-colors" />
                          </Link>
                        </motion.div>
                      ))}
                    </AnimatePresence>
                  </div>
                </div>
              )}

              {/* Products Section */}
              {hasResults && isSearching ? (
                // Show loading state while searching
                <div className="flex flex-col items-center justify-center py-16 gap-4">
                  <div className="gold-bar-loader" />
                  <p className="text-muted-foreground text-sm">מחפש...</p>
                </div>
              ) : displayExact.length > 0 ? (
                <div className="space-y-6">
                  {/* Exact matches */}
                  <div>
                    {hasResults && hasSearched && (
                      <h3 className="text-sm font-medium text-muted-foreground mb-2 sm:mb-3 text-right">
                        {displayExact.length === 1 ? 'נמצא מוצר אחד' : `נמצאו ${displayExact.length} מוצרים`} עבור "{searchQuery}"
                      </h3>
                    )}
                    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2 sm:gap-4 p-1">
                      <AnimatePresence mode="popLayout">
                        {displayExact.map((product, index) => (
                          <motion.div
                            key={product.id}
                            initial={{ opacity: 0, scale: 0.9 }}
                            animate={{ opacity: 1, scale: 1 }}
                            exit={{ opacity: 0, scale: 0.9 }}
                            transition={{ duration: 0.2, delay: index * 0.03 }}
                          >
                            <Link
                              to={`/products/${product.handle}`}
                              onClick={handleClose}
                              className="group block bg-background rounded-lg border border-border/50 overflow-hidden hover:border-primary/50 hover:shadow-lg transition-all duration-300"
                            >
                              <div className="aspect-square bg-secondary/10 overflow-hidden">
                                {product.images?.edges?.[0]?.node ? (
                                  <img
                                    src={product.images.edges[0].node.url}
                                    alt={product.title}
                                    loading="lazy"
                                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                                  />
                                ) : (
                                  <div className="w-full h-full flex items-center justify-center text-muted-foreground">
                                    <Search className="h-6 w-6 sm:h-8 sm:w-8" />
                                  </div>
                                )}
                              </div>
                              <div className="p-2 sm:p-3 text-right">
                                <h4 className="font-light text-xs sm:text-sm line-clamp-2 mb-1 group-hover:text-primary transition-colors">
                                  {product.title}
                                </h4>
                                <p className="text-primary font-medium text-xs sm:text-sm">
                                  {formatPrice(product.priceRange.minVariantPrice.amount, product.priceRange.minVariantPrice.currencyCode)}
                                </p>
                              </div>
                            </Link>
                          </motion.div>
                        ))}
                      </AnimatePresence>
                    </div>
                  </div>

                  {/* Related products section - show when few exact matches */}
                  {showRelatedSection && (
                    <div className="border-t pt-4">
                      <h3 className="text-sm font-medium text-muted-foreground mb-2 sm:mb-3 text-right">
                        מוצרים קשורים שעשויים לעניין אותך
                      </h3>
                      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2 sm:gap-4 p-1">
                        <AnimatePresence mode="popLayout">
                          {displayRelated.slice(0, 8).map((product, index) => (
                            <motion.div
                              key={product.id}
                              initial={{ opacity: 0, scale: 0.9 }}
                              animate={{ opacity: 1, scale: 1 }}
                              exit={{ opacity: 0, scale: 0.9 }}
                              transition={{ duration: 0.2, delay: index * 0.03 }}
                            >
                              <Link
                                to={`/products/${product.handle}`}
                                onClick={handleClose}
                                className="group block bg-background rounded-lg border border-border/50 overflow-hidden hover:border-primary/50 hover:shadow-lg transition-all duration-300"
                              >
                                <div className="aspect-square bg-secondary/10 overflow-hidden">
                                  {product.images?.edges?.[0]?.node ? (
                                    <img
                                      src={product.images.edges[0].node.url}
                                      alt={product.title}
                                      loading="lazy"
                                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                                    />
                                  ) : (
                                    <div className="w-full h-full flex items-center justify-center text-muted-foreground">
                                      <Search className="h-6 w-6 sm:h-8 sm:w-8" />
                                    </div>
                                  )}
                                </div>
                                <div className="p-2 sm:p-3 text-right">
                                  <h4 className="font-light text-xs sm:text-sm line-clamp-2 mb-1 group-hover:text-primary transition-colors">
                                    {product.title}
                                  </h4>
                                  <p className="text-primary font-medium text-xs sm:text-sm">
                                    {formatPrice(product.priceRange.minVariantPrice.amount, product.priceRange.minVariantPrice.currencyCode)}
                                  </p>
                                </div>
                              </Link>
                            </motion.div>
                          ))}
                        </AnimatePresence>
                      </div>
                    </div>
                  )}
                </div>
              ) : hasResults && hasSearched && !isSearching && displayRelated.length > 0 ? (
                // No exact matches but have related products
                <div className="space-y-4">
                  <div className="text-center py-4 sm:py-6 bg-muted/30 rounded-lg">
                    <p className="text-muted-foreground text-sm sm:text-base">
                      לא נמצאו תוצאות מדויקות עבור "{searchQuery}"
                    </p>
                    <p className="text-xs sm:text-sm text-muted-foreground/70 mt-1">
                      אבל יש לנו מוצרים קשורים{fallbackTerm ? ` ל"${fallbackTerm}"` : ''}:
                    </p>
                  </div>
                  <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2 sm:gap-4 p-1">
                    <AnimatePresence mode="popLayout">
                      {displayRelated.map((product, index) => (
                        <motion.div
                          key={product.id}
                          initial={{ opacity: 0, scale: 0.9 }}
                          animate={{ opacity: 1, scale: 1 }}
                          exit={{ opacity: 0, scale: 0.9 }}
                          transition={{ duration: 0.2, delay: index * 0.03 }}
                        >
                          <Link
                            to={`/products/${product.handle}`}
                            onClick={handleClose}
                            className="group block bg-background rounded-lg border border-border/50 overflow-hidden hover:border-primary/50 hover:shadow-lg transition-all duration-300"
                          >
                            <div className="aspect-square bg-secondary/10 overflow-hidden">
                              {product.images?.edges?.[0]?.node ? (
                                <img
                                  src={product.images.edges[0].node.url}
                                  alt={product.title}
                                  loading="lazy"
                                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                                />
                              ) : (
                                <div className="w-full h-full flex items-center justify-center text-muted-foreground">
                                  <Search className="h-6 w-6 sm:h-8 sm:w-8" />
                                </div>
                              )}
                            </div>
                            <div className="p-2 sm:p-3 text-right">
                              <h4 className="font-light text-xs sm:text-sm line-clamp-2 mb-1 group-hover:text-primary transition-colors">
                                {product.title}
                              </h4>
                              <p className="text-primary font-medium text-xs sm:text-sm">
                                {formatPrice(product.priceRange.minVariantPrice.amount, product.priceRange.minVariantPrice.currencyCode)}
                              </p>
                            </div>
                          </Link>
                        </motion.div>
                      ))}
                    </AnimatePresence>
                  </div>
                </div>
              ) : hasResults && hasSearched && !isSearching ? (
                // No results at all
                <div className="text-center py-12 sm:py-16">
                  <Search className="h-10 w-10 sm:h-12 sm:w-12 mx-auto text-muted-foreground/50 mb-4" />
                  <p className="text-muted-foreground text-sm sm:text-base">לא נמצאו תוצאות עבור "{searchQuery}"</p>
                  <p className="text-xs sm:text-sm text-muted-foreground/70 mt-2">נסה לחפש מילה אחרת</p>
                  {hasActiveFilters && (
                    <Button
                      variant="link"
                      onClick={clearFilters}
                      className="mt-3 text-primary"
                    >
                      נסה לנקות את הסינון
                    </Button>
                  )}
                </div>
              ) : (
                <div className="space-y-4">

                  {displayProducts.length > 0 ? (
                    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2 sm:gap-4 p-1">
                      <AnimatePresence mode="popLayout">
                        {displayProducts.slice(0, 12).map((product, index) => (
                          <motion.div
                            key={product.id}
                            initial={{ opacity: 0, scale: 0.9 }}
                            animate={{ opacity: 1, scale: 1 }}
                            exit={{ opacity: 0, scale: 0.9 }}
                            transition={{ duration: 0.2, delay: index * 0.03 }}
                          >
                            <Link
                              to={`/products/${product.handle}`}
                              onClick={handleClose}
                              className="group block bg-background rounded-lg border border-border/50 overflow-hidden hover:border-primary/50 hover:shadow-lg transition-all duration-300"
                            >
                              <div className="aspect-square bg-secondary/10 overflow-hidden">
                                {product.images?.edges?.[0]?.node ? (
                                  <img
                                    src={product.images.edges[0].node.url}
                                    alt={product.title}
                                    loading="lazy"
                                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                                  />
                                ) : (
                                  <div className="w-full h-full flex items-center justify-center text-muted-foreground">
                                    <Search className="h-6 w-6 sm:h-8 sm:w-8" />
                                  </div>
                                )}
                              </div>
                              <div className="p-2 sm:p-3 text-right">
                                <h4 className="font-light text-xs sm:text-sm line-clamp-2 mb-1 group-hover:text-primary transition-colors">
                                  {product.title}
                                </h4>
                                <p className="text-primary font-medium text-xs sm:text-sm">
                                  {formatPrice(
                                    product.priceRange.minVariantPrice.amount,
                                    product.priceRange.minVariantPrice.currencyCode
                                  )}
                                </p>
                              </div>
                            </Link>
                          </motion.div>
                        ))}
                      </AnimatePresence>
                    </div>
                  ) : (
                    <div className="text-center py-6 sm:py-8 text-muted-foreground text-sm">
                      לא הצלחנו לטעון מוצרים כרגע
                    </div>
                  )}

                  <div className="text-center text-muted-foreground/80 text-xs sm:text-sm">
                    התחל להקליד כדי לחפש מוצרים
                  </div>
                </div>
              )}
            </>
          )}
        </div>
        </>
        )}
      </DialogContent>
    </Dialog>
  );
};
